/**
 * PLACEHOLDER analytics data for the Study Results screen.
 *
 * No aggregated-emotion / per-moment endpoint exists in the backend yet, so
 * EVERYTHING in this file is mock data ported from the design mock
 * `claude_design/researcher/r-results.jsx`. Only the study title and the
 * respondent count (N) on the results header come from the real API.
 *
 * Emotion keys (joy/surprise/attention/neutral/sadness/anger/disgust) map to
 * `theme.affexy.emotion` colors and to the `results:emotion.*` i18n labels.
 */

export type EmotionKey =
  | 'joy'
  | 'surprise'
  | 'attention'
  | 'neutral'
  | 'sadness'
  | 'anger'
  | 'disgust'

/** The 7 emotion legend order, matching the mock. */
export const EMOTION_ORDER: EmotionKey[] = [
  'joy',
  'surprise',
  'attention',
  'neutral',
  'sadness',
  'anger',
  'disgust',
]

// ── Dominant strip ────────────────────────────────────────────────────────
// 100 segments along the 30s clip; each is the dominant emotion at that moment.
export const DOMINANT_STRIP: EmotionKey[] = [
  ...Array<EmotionKey>(8).fill('attention'),
  ...Array<EmotionKey>(4).fill('surprise'),
  ...Array<EmotionKey>(18).fill('joy'),
  ...Array<EmotionKey>(8).fill('attention'),
  ...Array<EmotionKey>(4).fill('joy'),
  ...Array<EmotionKey>(6).fill('attention'),
  ...Array<EmotionKey>(8).fill('joy'),
  ...Array<EmotionKey>(12).fill('neutral'),
  ...Array<EmotionKey>(8).fill('joy'),
  ...Array<EmotionKey>(8).fill('attention'),
  ...Array<EmotionKey>(8).fill('joy'),
  ...Array<EmotionKey>(8).fill('attention'),
].slice(0, 100)

// ── Synchronized emotion timeline: 7 series, 0..1 values per second 0..30 ──
export const EMOTION_SERIES: { k: EmotionKey; d: number[] }[] = [
  {
    k: 'joy',
    d: [0.18, 0.22, 0.34, 0.46, 0.62, 0.74, 0.82, 0.86, 0.78, 0.68, 0.74, 0.82, 0.86, 0.72, 0.58, 0.5, 0.46, 0.42, 0.38, 0.36, 0.42, 0.5, 0.62, 0.72, 0.82, 0.88, 0.84, 0.74, 0.68, 0.6, 0.52],
  },
  {
    k: 'attention',
    d: [0.42, 0.48, 0.56, 0.62, 0.68, 0.74, 0.78, 0.8, 0.78, 0.74, 0.7, 0.72, 0.74, 0.76, 0.74, 0.72, 0.68, 0.66, 0.6, 0.56, 0.58, 0.62, 0.66, 0.72, 0.78, 0.82, 0.78, 0.7, 0.64, 0.58, 0.52],
  },
  {
    k: 'surprise',
    d: [0.08, 0.1, 0.18, 0.32, 0.48, 0.38, 0.28, 0.22, 0.18, 0.16, 0.14, 0.16, 0.18, 0.16, 0.14, 0.12, 0.14, 0.16, 0.14, 0.12, 0.1, 0.12, 0.16, 0.22, 0.28, 0.34, 0.28, 0.22, 0.18, 0.14, 0.1],
  },
  {
    k: 'neutral',
    d: [0.58, 0.54, 0.48, 0.42, 0.36, 0.32, 0.3, 0.28, 0.3, 0.34, 0.32, 0.3, 0.28, 0.3, 0.34, 0.38, 0.42, 0.46, 0.5, 0.52, 0.5, 0.46, 0.42, 0.38, 0.34, 0.32, 0.36, 0.42, 0.46, 0.5, 0.52],
  },
  {
    k: 'sadness',
    d: [0.04, 0.06, 0.06, 0.08, 0.06, 0.08, 0.1, 0.08, 0.06, 0.1, 0.12, 0.08, 0.06, 0.08, 0.1, 0.12, 0.1, 0.08, 0.06, 0.06, 0.08, 0.1, 0.08, 0.06, 0.06, 0.08, 0.1, 0.08, 0.08, 0.1, 0.08],
  },
  {
    k: 'anger',
    d: [0.02, 0.02, 0.02, 0.02, 0.04, 0.04, 0.04, 0.06, 0.06, 0.04, 0.04, 0.02, 0.04, 0.04, 0.06, 0.04, 0.04, 0.02, 0.04, 0.04, 0.06, 0.04, 0.04, 0.02, 0.02, 0.04, 0.04, 0.06, 0.04, 0.04, 0.04],
  },
  {
    k: 'disgust',
    d: [0.02, 0.02, 0.04, 0.04, 0.04, 0.02, 0.04, 0.06, 0.06, 0.04, 0.04, 0.06, 0.04, 0.02, 0.02, 0.04, 0.06, 0.08, 0.06, 0.04, 0.04, 0.02, 0.02, 0.02, 0.04, 0.04, 0.06, 0.04, 0.02, 0.04, 0.04],
  },
]

/** Playhead position (0..1) — 23s of 30 → 0.77. */
export const PLAYHEAD = 0.77
export const PLAYHEAD_TIME = '00:23'
export const CLIP_DURATION_LABEL = '00:30'

/** Tooltip values shown at the playhead. */
export const PLAYHEAD_TOOLTIP = [
  { k: 'joy' as EmotionKey, pct: 82, muted: false },
  { k: 'attention' as EmotionKey, pct: 78, muted: false },
  { k: 'neutral' as EmotionKey, pct: 34, muted: true },
  { k: 'sadness' as EmotionKey, pct: 6, muted: true },
]

// ── Key moments ─────────────────────────────────────────────────────────
export type KeyMoment = {
  t: string
  textKey: string
  subKey: string
  emotion: EmotionKey
  delta: 'up' | 'down'
  current?: boolean
}
export const KEY_MOMENTS: KeyMoment[] = [
  { t: '0:04', textKey: 'm1', subKey: 'm1sub', emotion: 'surprise', delta: 'up' },
  { t: '0:08', textKey: 'm2', subKey: 'm2sub', emotion: 'joy', delta: 'up' },
  { t: '0:13', textKey: 'm3', subKey: 'm3sub', emotion: 'attention', delta: 'down' },
  { t: '0:23', textKey: 'm4', subKey: 'm4sub', emotion: 'joy', delta: 'up', current: true },
  { t: '0:27', textKey: 'm5', subKey: 'm5sub', emotion: 'attention', delta: 'up' },
]

// ── Auto insights ─────────────────────────────────────────────────────────
export type InsightKind = 'positive' | 'warning' | 'neutral'
export const AUTO_INSIGHTS: { kind: InsightKind; textKey: string }[] = [
  { kind: 'positive', textKey: 'i1' },
  { kind: 'warning', textKey: 'i2' },
  { kind: 'neutral', textKey: 'i3' },
]

// ── Donut: aggregate emotion mix ───────────────────────────────────────────
export const DONUT_SLICES: { k: EmotionKey; pct: number }[] = [
  { k: 'joy', pct: 38 },
  { k: 'attention', pct: 27 },
  { k: 'neutral', pct: 18 },
  { k: 'surprise', pct: 10 },
  { k: 'sadness', pct: 4 },
  { k: 'disgust', pct: 2 },
  { k: 'anger', pct: 1 },
]
export const DONUT_DOMINANT: EmotionKey = 'joy'
export const DONUT_DOMINANT_PCT = 38

// ── Attention curve ─────────────────────────────────────────────────────
export const ATTENTION_CURVE = [62, 68, 74, 78, 80, 78, 74, 70, 72, 74, 76, 74, 68, 60, 56, 62, 66, 72, 78, 82, 78, 70]
export const ATTENTION_AVG = 71

// ── Benchmark vs norm ─────────────────────────────────────────────────────
export const BENCHMARK_ROWS: { labelKey: string; a: number; b: number; d: string }[] = [
  { labelKey: 'joy', a: 38, b: 28, d: '+10 pp.' },
  { labelKey: 'attention', a: 71, b: 65, d: '+6 pp.' },
  { labelKey: 'surprise', a: 14, b: 18, d: '−4 pp.' },
  { labelKey: 'neutral', a: 18, b: 24, d: '−6 pp.' },
]

// ── Question results ─────────────────────────────────────────────────────
export const Q1_DIST = [3, 5, 14, 38, 27] // 87 total, scale 1..5
export const Q1_HEADLINE_PCT = '75%'

export const Q2_OPTIONS: { labelKey: string; n: number }[] = [
  { labelKey: 'q2o1', n: 28 },
  { labelKey: 'q2o2', n: 22 },
  { labelKey: 'q2o3', n: 18 },
  { labelKey: 'q2o4', n: 11 },
  { labelKey: 'q2o5', n: 6 },
  { labelKey: 'q2o6', n: 2 },
]
export const Q2_HEADLINE_PCT = '32%'

export const Q3_NPS = { detractors: 12, passives: 36, promoters: 39 }

// Free-text word cloud. Words stay raw (respondent verbatim) — not translated.
export const Q4_WORDS: { w: string; s: number }[] = [
  { w: 'rodzina', s: 38 }, { w: 'ciepło', s: 30 }, { w: 'dom', s: 28 }, { w: 'naturalnie', s: 22 },
  { w: 'sok', s: 20 }, { w: 'jesień', s: 16 }, { w: 'wspomnienia', s: 14 }, { w: 'smak', s: 12 },
  { w: 'jabłko', s: 12 }, { w: 'mama', s: 10 }, { w: 'śniadanie', s: 9 }, { w: 'witamina', s: 9 },
  { w: 'świeży', s: 8 }, { w: 'zdrowy', s: 7 }, { w: 'pamiętam', s: 6 }, { w: 'dzieciństwo', s: 6 }, { w: 'sad', s: 5 },
]
export const Q4_HEADLINE_PCT = '74%'

// ── Per-respondent grid ───────────────────────────────────────────────────
export const RESPONDENT_CELLS = Array.from({ length: 24 }).map((_, i) => {
  const seed = (i * 37 + 11) % 100
  const dominant: EmotionKey =
    seed < 40 ? 'joy' : seed < 60 ? 'attention' : seed < 75 ? 'neutral' : seed < 85 ? 'surprise' : 'sadness'
  return { dominant, score: 0.4 + (seed / 100) * 0.5 }
})

// ── MULTI variant ─────────────────────────────────────────────────────────
export type MultiVersion = {
  nameKey: string
  n: number
  color: string
  letter: string
  winner: boolean
  joy: number
  att: number
  completion: number
  // mini timeline joy signature
  shape: number[]
}

const MULTI_SHAPES = [
  [0.3, 0.42, 0.5, 0.56, 0.62, 0.7, 0.66, 0.6, 0.52, 0.48, 0.5, 0.54, 0.58, 0.62, 0.66, 0.7, 0.64, 0.56, 0.5, 0.46, 0.48],
  [0.4, 0.5, 0.58, 0.66, 0.74, 0.8, 0.84, 0.82, 0.78, 0.74, 0.7, 0.72, 0.76, 0.8, 0.84, 0.86, 0.8, 0.74, 0.68, 0.6, 0.54],
  [0.5, 0.62, 0.7, 0.76, 0.72, 0.66, 0.6, 0.54, 0.5, 0.48, 0.5, 0.54, 0.58, 0.62, 0.66, 0.64, 0.6, 0.54, 0.5, 0.46, 0.42],
]

// Version accent colors come from the emotion palette (sadness=blue, joy=coral,
// surprise=amber) to avoid hardcoded hex — see StudyResults where they're read
// from theme.affexy.emotion. Here we store the emotion key instead of the hex.
export const MULTI_VERSIONS: (Omit<MultiVersion, 'color'> & { colorKey: EmotionKey })[] = [
  { nameKey: 'a', n: 41, colorKey: 'sadness', letter: 'A', winner: false, joy: 58, att: 64, completion: 78, shape: MULTI_SHAPES[0] },
  { nameKey: 'b', n: 43, colorKey: 'joy', letter: 'B', winner: true, joy: 72, att: 78, completion: 84, shape: MULTI_SHAPES[1] },
  { nameKey: 'c', n: 40, colorKey: 'surprise', letter: 'C', winner: false, joy: 64, att: 56, completion: 70, shape: MULTI_SHAPES[2] },
]

export const MULTI_N = 124

// Summary table. Values that include @times are kept verbatim (data, not copy).
export type MultiRow = { labelKey: string; a: string; b: string; c: string; w: 'a' | 'b' | 'c' }
export const MULTI_ROWS: MultiRow[] = [
  { labelKey: 'avgJoy', a: '58%', b: '72%', c: '64%', w: 'b' },
  { labelKey: 'avgAttention', a: '64%', b: '78%', c: '56%', w: 'b' },
  { labelKey: 'joyPeak', a: '76% @ 0:18', b: '88% @ 0:23', c: '74% @ 0:12', w: 'b' },
  { labelKey: 'attentionDrop', a: '@ 0:14 −22%', b: '@ 0:09 −12%', c: '@ 0:18 −26%', w: 'b' },
  { labelKey: 'nps', a: '+22', b: '+38', c: '+18', w: 'b' },
  { labelKey: 'completionRate', a: '78%', b: '84%', c: '70%', w: 'b' },
  { labelKey: 'avgWatch', a: '0:24', b: '0:28', c: '0:22', w: 'b' },
]

export const MULTI_INSIGHTS: { kind: InsightKind; textKey: string }[] = [
  { kind: 'positive', textKey: 'i1' },
  { kind: 'warning', textKey: 'i2' },
  { kind: 'neutral', textKey: 'i3' },
]
